import { Vector2 } from "peet-math";
import { AddToCartRequest, AddTicketsToCartResult, AddToCartPackageRequest, AddPackageTicketsToCartResult, AddToCartRequestBase } from "./models/buyticket/ticketselection";
import { FindTicketsAtPintpointResult, FindTicketsResult } from "./models/buyticket/findticketsresult";
import { TicketModel } from "./models/buyticket/submodels";
import { EventDateInfo } from "./models/common";
import { PackageInfoModel } from "./models/common/package-info-model";
import { PriceListResponse, TicketSelectionManagementModel } from "./models/buyticket";
import { BuyPackagePromoInfoModel, BuyPackagePromoInfoResponseModel } from "./models/buypackage/buy-package-promo-info-model";
import { FindPackageTicketsResult } from "./models/buypackage/find-package-tickets-result";


/**
 * Event date purchase info model
 */
export async function getEventDateInfo(eventDateId: number): Promise<EventDateInfo> {
    const result = await tsapp.mvcapi.postCtl("buyticket/eventdateinfos", { eventDateID: eventDateId }, { cache: false });
    return result.EventDateInfo;
}

export async function getPackageInfo(packageId: number): Promise<PackageInfoModel> {
    const result = await tsapp.mvcapi.postCtl("buypackage/packageinfos", { packageID: packageId }, { cache: false });
    return result.PackageInfo;
}

export async function getPackagePresaleInfoByKey(presaleKey: string): Promise<BuyPackagePromoInfoModel> {
    const result = await tsapp.mvcapi.postCtl("buypackage/promoinfo", { rstprv: presaleKey }) as BuyPackagePromoInfoResponseModel;
    return result.promoInfo;
}

export async function getEventDatePriceList(eventDateIDs: number[], packageID?: number): Promise<PriceListResponse> {
    return await tsapp.mvcapi.postCtl("chooseticketprice/pricelist", { eventDateIDs, packageID });
}




/**
 * Clear of tickets that havn't been add to the cart
 * This ajax call is not async because it must be called before leaving places.
 */
export async function clearUnselectedTickets(): Promise<void> {
    await tsapp.mvcapi.postCtl("buyticket/clearunselectedtickets", null, { async: false });
}


/**
 * Selects specific tickets.
 * @param eventDateId The event date to search tickets for
 * @param ticketIds The selected ticket ids
 * @returns A tuple of found tickets and error message if necessary.
 */
export async function selectSpecificTickets(eventDateId: number, ticketIds: number[]): Promise<[TicketModel[], string]> {
    try {
        const result = await tsapp.mvcapi.postCtl("ticket/selectspecifictickets", { eventDateID: eventDateId, ticketIds: ticketIds });
        return [result.tickets, null];
    } catch (err) {
        return [[], err.message];
    }
}

/**
 * Selects specific tickets.
 * @param eventDateId The event date in which we clicked those seat
 * @param ticketIds The selected ticket ids
 * @param selectedDateIds The selected date in the package
 * @returns A tuple of found tickets and error message if necessary.
 */
export async function selectPackageSeatFromTicketIds(packageId: number, baseEventDateId: number, ticketIds: number[], selectedDateIds: number[]): Promise<[TicketModel[], string]> {
    try {
        const result = await tsapp.mvcapi.postCtl("ticket/selectspecificpackagetickets", { 
            packageId,
            baseEventDateId, 
            ticketIds,
            selectedDateIds 
        });
        return [result.tickets, null];
    } catch (err) {
        return [[], err.message];
    }
}

export async function unselectSpecificTickets(ticketIds: number[]): Promise<void> {
    await tsapp.mvcapi.postCtl("ticket/unselectspecifictickets", { ticketIds: ticketIds });
}

export async function getTicketSelectionInfo(addToCartRequest: AddToCartRequestBase, selectedEventDateIds: number[], packageId: number = null): Promise<TicketSelectionManagementModel> {
    if(packageId == null) 
        return await tsapp.mvcapi.postCtl('chooseticketprice/ticketselectioninfos', { 
            addToCartRequest: addToCartRequest as AddToCartRequest,
            selectedEventDateIds: selectedEventDateIds
        });
    else
        return await tsapp.mvcapi.postCtl('chooseticketprice/packageticketselectioninfos', { 
            packageID: packageId,
            selectedEventDateIds: selectedEventDateIds,
            addToCartRequest: addToCartRequest as AddToCartPackageRequest
        });
}

// Returns { IsSuccess = bool, Message = string } *************************************************************************
export async function findTickets(eventDateID: number, desiredTicketQty: number, venueSectionID?: number, ticketName?: string, clearPreviousSelection: boolean = true): Promise<FindTicketsResult> {
    try {
        const result = await tsapp.mvcapi.postCtl('buyticket/findtickets', {
            eventDateID,
            desiredTicketQty,
            venueSectionID,
            ticketName,
            clearPreviousSelection
        });
        return result;
    } catch (err) {
        return err;
    }
}

// Returns { IsSuccess = bool, Message = string } *************************************************************************
export async function findPackageTickets(packageId: number, eventDateIds: number[], desiredTicketQty: number, venueSectionNames?: string[], clearPreviousSelection: boolean = true): Promise<FindPackageTicketsResult> {
    try {
        const result = await tsapp.mvcapi.postCtl('buypackage/findtickets', {
            packageId,
            eventDateIds,
            desiredTicketQty,
            venueSectionNames,
            clearPreviousSelection
        }) as FindPackageTicketsResult;
        return result;
    } catch (err) {
        return err;
    }
}

// For multidate packages :
/*
handleVenuePlanSearchTicket(packageKit, selectedEventDateID, nbTickets, atPosition, planEngine){
if(packageKit.packageID != null)
    this.searchTicketRequest = tsapp.mvcapi.postCtl("ticket/searchpackageticketatpinpoint", { packageID: packageKit.packageID,
                                                                                                eventDateIDs: packageKit.eventDateIDs,
                                                                                                selectedEventDateID: selectedEventDateID,
                                                                                                nbOfTickets: nbTickets,
                                                                                                point: { X: atPosition.x, Y: atPosition.y }})
                                    .success(function (data) {
                                        this.props.afterSearchTickets();
                                    }.bind(this));
else
*/

export async function findTicketsAtPinPoint(eventDateID: number, desiredTicketQty: number, atPoint: Vector2, clearPreviousSelection: boolean = true): Promise<FindTicketsAtPintpointResult> {
    try {
        
        return await tsapp.mvcapi.postCtl("ticket/searchticketatpinpoint", { 
            eventDateID: eventDateID, 
            nbOfTickets: desiredTicketQty, 
            point: { X: atPoint.x, Y: atPoint.y },
            clearPreviousSelection
        });
    } catch (err) {
        return err;
    }
}

export async function findPackageTicketsAtPinPoint(packageId: number, selectedEventDateID: number, eventDateIds: number[], desiredTicketQty: number, atPoint: Vector2, clearPreviousSelection: boolean = true): Promise<FindTicketsAtPintpointResult> {
    try {
        
        return await tsapp.mvcapi.postCtl("ticket/searchpackageticketatpinpoint", { 
            packageId: packageId,
            eventDateIDs: eventDateIds,
            selectedEventDateID: selectedEventDateID,
            nbOfTickets: desiredTicketQty, 
            point: { X: atPoint.x, Y: atPoint.y },
            clearPreviousSelection
        });
    } catch (err) {
        return err;
    }
}



export async function addTicketsToCartNewSale(addToCartRequest: AddToCartRequest): Promise<AddTicketsToCartResult> {
    try {
        const result = await tsapp.mvcapi.postCtl('buyticket/addticketstocartnewsale', addToCartRequest);
        console.log('addTicketsToCartNewSale', result);
        return result;
    } catch (err) {
        return err;
    }
}

export async function addPackageToCartNewSale(addToCartRequest: AddToCartPackageRequest): Promise<AddPackageTicketsToCartResult> {
    try {
        const result = await tsapp.mvcapi.postCtl('buypackage/addticketstocartnewsale', addToCartRequest);
        console.log('addPackageToCartNewSale', result);
        return result;
    } catch (err) {
        return err;
    }
}